import { useMemo } from "react";
import * as Yup from "yup";
import { ContactFormSchema, contactFormInitialValues, IContactForm, onSubmitContactForm } from "@/pages/checkout/components/ContactForm";
import { LocationFormSchema, locationFormInitialValues, ILocationForm, onSubmitLocationForm } from "@/pages/checkout/components/LocationForm";
import { PaymentFormSchema, paymentFormInitialValues, IPaymentForm } from "@/pages/checkout/components/PaymentForm";
import { CheckoutSharedProps } from "@/pages/checkout/CheckoutPage";

export type ICheckout = IContactForm & ILocationForm & IPaymentForm;
export interface Step {
  id: string;
  title: string;
  schema?: Yup.AnyObject;
  initialValues?: IContactForm | ILocationForm | IPaymentForm;
  onSubmit?: (values: ICheckout, sharedProps: CheckoutSharedProps) => void;
}

const CONTACT_STEP: Step = {
  id: "contact",
  title: "Datos de contacto",
  schema: ContactFormSchema,
  initialValues: contactFormInitialValues,
  onSubmit: onSubmitContactForm,
};

const LOCATION_STEP: Step = {
  id: "location",
  title: "Ubicación",
  schema: LocationFormSchema,
  initialValues: locationFormInitialValues,
  onSubmit: onSubmitLocationForm,
};

const PAYMENT_STEP: Step = {
  id: "payment",
  title: "Métodos de Pago",
  schema: PaymentFormSchema,
  initialValues: paymentFormInitialValues,
};

const CONFIRMATION_STEP: Step = {
  id: "confirmation",
  title: "Confirmación",
};

const useCheckoutSteps = () => {
  const steps = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const activeFlow = qs.get("flow");

    switch (activeFlow) {
      case "delivery":
        return [CONTACT_STEP, LOCATION_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
      case "pick-up":
        return [CONTACT_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
      case "reservations":
        return [CONTACT_STEP, CONFIRMATION_STEP];
      case "dine-in":
        return [CONTACT_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
      default:
        return [CONTACT_STEP, LOCATION_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
    }
  }, []);

  const stepId = location.pathname.split("/").pop();

  const activeStep = useMemo(() => {
    const step = steps.find((step) => step.id === stepId);
    return step ?? null;
  }, [steps, stepId]);

  const isFirstStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    return index === 0;
  }, [steps, stepId]);

  const isLastStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    return index === steps.length - 1;
  }, [steps, stepId]);

  const previousStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    if (index === 0) return steps[0];
    return steps[index - 1];
  }, [steps, stepId]);

  const nextStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    if (index === steps.length - 1) return steps[steps.length - 1];
    return steps[index + 1];
  }, [steps, stepId]);

  const initialValues = useMemo(() => {
    if (!steps.length) return {};

    return steps.reduce(
      (acc, curr) => ({
        ...acc,
        ...(curr?.initialValues ?? {}),
      }),
      {} as ICheckout
    );
  }, [steps]);

  const validationSchema = useMemo(() => {
    if (isLastStep) {
      const draft = [...steps];
      draft.pop();

      return draft.reduce((acc, curr) => acc.concat(curr.schema), Yup.object().shape({}));
    }

    const step = steps.find((step) => step.id === stepId);
    return step?.schema;
  }, [isLastStep, steps, stepId]);

  return {
    steps,
    stepId,
    activeStep,
    isFirstStep,
    isLastStep,
    previousStep,
    nextStep,
    initialValues,
    validationSchema,
  };
};

export default useCheckoutSteps;
